import { useCallback, useEffect, useState } from 'react';

import { WizardHeader } from '@bambi/component-library/src/components/Wizard/deprecated/WizardHeader';
import { pick } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'src/app/store/hooks';
import { DeleteAction } from 'src/common/DeleteAction';
import { DeleteModal } from 'src/common/DeleteModal';
import {
  DriverRead,
  useDriversCreateMutation,
  useDriversDestroyMutation,
  useDriversListQuery,
  useDriversPartialUpdateMutation,
  useDriversUndeleteCreateMutation,
  useMembersRetrieveQuery,
  useMembersUndeleteCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { useConfirmClose } from 'src/common/modals/RequiresConfirmationModal/RequiresConfirmationModal';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { convertDriverProfileToDriver } from '../driver-profile/convertDriverProfileToDriver';
import { DriverProfile } from '../driver-profile/DriverProfile';
import { setShowDriverProfileModal } from '../team.slice';
import { assignDefaultFormValues } from './assignDefaultFormValues';
import { DriverProfileFormStep1 } from './DriverProfileFormStep1/DriverProfileFormStep1';
import { DriverProfileFormStep2 } from './DriverProfileFormStep2/DriverProfileFormStep2';
import { generateSuccessToast } from './generateAddSuccessToast';
import { generateDeleteSuccessToast } from './generateDeleteSuccessToast';
import { generateEditSuccessToast } from './generateEditSuccessToast';

function getStepTitle(step: number, driverProfile: DriverProfile | null) {
  switch (step) {
    case 2:
      return 'Add Skills';
    default:
      return driverProfile ? 'Edit Driver Profile' : 'Add Driver Profile';
  }
}

export function DriverProfileForm() {
  const currentOrganizationId = useAppSelector(
    (state) => state.auth.currentOrganizationId
  );
  const dispatch = useDispatch();
  const open = useAppSelector((state) => state.team.showDriverProfileModal);

  const driverProfile = useAppSelector(
    (state) => state.team.selectedDriverProfile
  );
  const nextToastId = useAppSelector((state) => state.toast.nextId);

  const [step, setStep] = useState(1);
  const [serverErrors, setServerErrors] = useState<string | null>(null);

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultFormValues = assignDefaultFormValues(driverProfile);
  const methods = useForm({
    defaultValues: defaultFormValues,
  });

  const [createDriver] = useDriversCreateMutation();
  const [updateDriver] = useDriversPartialUpdateMutation();
  const [deleteDriver] = useDriversDestroyMutation();

  // Restore driver/membership hooks
  const { data: deletedDrivers } = useDriversListQuery(
    {
      id: methods.getValues().member_id,
      deletedObjects: 'include',
    },
    { skip: !methods.getValues().member_id }
  );
  const [undeleteDriver] = useDriversUndeleteCreateMutation();
  const { data: deletedMembership } = useMembersRetrieveQuery(
    {
      id: deletedDrivers?.results[0]?.membership ?? '',
      deletedObjects: 'include',
    },
    {
      skip: !deletedDrivers?.results[0]?.membership,
    }
  );
  const [undeleteMembership] = useMembersUndeleteCreateMutation();

  const onSubmitStepTwo = async () => {
    setIsSubmitting(true);
    setServerErrors(null);

    let isUpdatingDriver = !!driverProfile?.id;
    const deletedDriver = deletedDrivers?.results[0];

    const patchedDriver: DriverRead = convertDriverProfileToDriver(
      deletedDriver
        ? {
            ...methods.getValues(),
            ...pick(deletedDriver, [
              'id',
              'first_name',
              'last_name',
              'membership',
            ]),
            // Using driver_id as the member_id when selecting the deleted
            // profile, so remap it here to provide the correct value
            member_id: deletedDriver.membership,
          }
        : methods.getValues(),
      currentOrganizationId ?? ''
    );
    // Restore the membership?
    if (deletedMembership?.deleted_at) {
      try {
        await undeleteMembership({
          id: deletedMembership.id,
          membership: deletedMembership,
        }).unwrap();
      } catch (e) {
        setServerErrors(
          `Failed to restore deleted account while updating: ${formatServerError(
            e
          )}.`
        );
        return;
      }
    }

    // Restoring a deleted driver with a new profile?
    if (deletedDriver) {
      try {
        await undeleteDriver({
          driver: deletedDriver,
          id: deletedDriver.id,
        }).unwrap();

        isUpdatingDriver = true;
      } catch (e) {
        setServerErrors(
          `Failed to restore deleted driver profile while updating: ${formatServerError(
            e
          )}.`
        );
        return;
      }
    }

    try {
      const driver = await (isUpdatingDriver
        ? updateDriver({
            id: deletedDriver ? deletedDriver.id : patchedDriver.id,
            patchedDriver,
          }).unwrap()
        : createDriver({
            driver: patchedDriver,
          }).unwrap());

      const showAction = driverProfile
        ? generateEditSuccessToast(nextToastId, driver.first_name || 'unknown')
        : generateSuccessToast(nextToastId);

      dispatch(show(showAction));
      dispatch(setShowDriverProfileModal(false));
      setStep(1);
    } catch (e) {
      const formattedErr = formatServerError(e);
      setServerErrors(formattedErr);
    } finally {
      setIsSubmitting(false);
    }
  };
  const title = getStepTitle(step, driverProfile);

  useEffect(() => {
    if (open) {
      methods.reset(defaultFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setModalOpen = useCallback(
    (open: boolean) => {
      dispatch(setShowDriverProfileModal(open));
    },
    [dispatch]
  );

  const onModalClose = useCallback(() => {
    setStep(1);
    setServerErrors(null);
    methods.reset();
  }, [methods]);

  const shouldConfirmClose = useCallback(
    () => methods.formState.isDirty,
    [methods.formState.isDirty]
  );

  const [setOpen, onClose] = useConfirmClose({
    setOpen: setModalOpen,
    shouldConfirmClose,
    onClose: onModalClose,
  });

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        onClose={onClose}
        onInteractOutside={(e) => {
          onClose();
        }}
        title={title}
      >
        <div data-testid="driver-profile-form">
          <WizardHeader
            title={title}
            wizardState={{ step, totalSteps: 2 }}
            setStep={setStep}
          />
          <FormProvider {...methods}>
            <form>
              {step === 1 ? (
                <DriverProfileFormStep1
                  onNextClick={() => setStep(2)}
                  onCancel={() => setOpen(false)}
                >
                  {driverProfile ? (
                    <DeleteAction
                      onDelete={() => {
                        setIsConfirmingDelete(true);
                        dispatch(setShowDriverProfileModal(false));
                      }}
                      label="Delete this driver profile"
                    />
                  ) : null}
                </DriverProfileFormStep1>
              ) : null}
              {step === 2 ? (
                <DriverProfileFormStep2
                  onSubmit={onSubmitStepTwo}
                  onCancel={() => setOpen(false)}
                  serverErrors={serverErrors}
                  isSubmitting={isSubmitting}
                />
              ) : null}
            </form>
          </FormProvider>
        </div>
      </Modal>
      <DeleteModal
        open={isConfirmingDelete}
        setOpen={setIsConfirmingDelete}
        onCancel={() => {
          dispatch(setShowDriverProfileModal(false));
          setIsConfirmingDelete(false);
        }}
        onConfirm={async () => {
          try {
            await deleteDriver({
              id: driverProfile?.id || '',
            }).unwrap();
            dispatch(show(generateDeleteSuccessToast(nextToastId)));
            setIsConfirmingDelete(false);
          } catch (error) {
            console.error(error);
          }
        }}
        title="Delete this driver profile?"
        description="Are you sure you want to delete this driver profile? Future assignments cannot be restored."
      />
    </>
  );
}
